/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect } from 'react';
import { FC, useCallback } from 'react';
import styled from 'styled-components';
import { FontFamily, useZakeke } from 'zakeke-configurator-react';
import { TextArea } from '../Atomic';
import {
  CSSObjectWithLabel,
  GroupBase,
  OptionProps,
  SingleValueProps,
  components,
} from 'react-select';
import { useState } from 'react';
import { debounce } from 'lodash';

import type { PropChangeHandler } from '../Layout/Designer';
import { FormControl } from './FormControl';
import AdvancedSelect from './AdvancedSelect';
import { P, PLACEHOLDERS } from '../../Placeholder';

export interface EditTextItem {
  guid: string;
  name: string;
  text: string;
  fillColor: string;
  fontFamily: string;
  fontWeight: string;
  fontSize: number;
  isTextOnPath: boolean;
  constraints: { [key: string]: any } | null;
}

// Define a mapping of hex codes to color names
const colorNameMap: { [key: string]: string } = {
  '#2a2b2c': 'Black',
  '#ffffff': 'White',
  '#fcd877': 'Gold',
  '#dae3e0': 'Silver',
  '#7e0000': 'Maroon',
  '#fe0002': 'Red',
  '#fda4f6': 'Pink',
  '#f62681': 'Electric Pink',
  '#fe3ef6': 'Magenta',
  '#810080': 'Purple',
  '#050953': 'Navy Blue',
  '#080ace': 'Royal Blue',
  '#0d92ff': 'Electric Blue',
  '#d0f6fb': 'Sky Blue',
  '#008080': 'Teal',
  '#00ff00': 'Fluorescent Green',
  '#61a631': 'Irish Green',
  '#1d340a': 'Dark Green',
  '#fcf000': 'Yellow',
  '#eefd02': 'Fluorescent Yellow',
  '#ffa300': 'Orange',
  '#113082': 'Blue',
  '#18538b': 'Aqua Blue',
  '#29773b': 'Green',
};

const defaultColorsPalette = ['#000000', '#FFFFFF'];

enum ItemType {
  Text = 0,
  Image = 1,
}

export interface TextItem {
  type: ItemType;
  areaId: number;
  guid: string;
  name: string;
  text: string;
  strokeColor: string;
  strokeWidth: number;
  fillColor: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  fontStyle: string | undefined;
  fontStretch: string;
  justification: string;
  isTextOnPath: boolean;
  constraints: {
    [key: string]: any;
  } | null;
}

const ItemTextContainer = styled.div`
  display: flex;
  flex-direction: row;
  grid-gap: 10px;
  width: 100%;
  overflow: hidden;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    grid-gap: 6px;
    justify-content: space-between;
  }
`;

const TextToolsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const TextButtonsContainer = styled.div`
  width: 50%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 5px;
`;

const ColorPickerContainer = styled.div`
  margin-right: 5px;
  width: calc(50% - 30px);
`;

export const ColorsContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 20px;
  flex-wrap: wrap; // optional if multiple rows needed
`;

export const ColorSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  row-gap: 10px; // optional for vertical spacing
`;

const OptionContainer = styled(components.Option)`
  background-color: white !important;

  &:hover {
    background-color: #ddd !important;
  }

  img {
    max-width: 100%;
    height: 4px;
    object-fit: contain;
  }
`;

const SingleValueContainer = styled(components.SingleValue)`
  max-width: 100%;
  height: 32px;
  object-fit: contain;
`;

export const SinglePaletteItem = styled.div<{ color: string; selected: boolean }>`
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  border: ${(props) => (props.selected ? '2px solid black' : '1px solid lightgray')};
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &:hover .tooltip {
    opacity: 1;
    visibility: visible;
  }

  ${(props) =>
    props.selected &&
    `
     &::after {
     content: url('data:image/svg+xml,<svg width="23" height="18" viewBox="0 0 23 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.3469 1.53854C19.3757 0.710969 20.8654 0.805708 21.7773 1.80129C22.7386 2.85135 22.6957 4.51072 21.6789 5.51481L21.6779 5.51479L10.7232 16.3118L10.7222 16.3128C9.70101 17.3124 8.09517 17.2747 7.12517 16.2411L1.42463 10.9921L1.40748 10.977L1.39234 10.96C0.434993 9.90463 0.488436 8.24559 1.50927 7.24696C2.52993 6.24851 4.13176 6.28523 5.10252 7.31563L9.03092 10.6997L18.1474 1.71604L18.3469 1.53854Z" fill="white" stroke="black"/></svg>');
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 20px;
      font-weight: bold;
      text-shadow: 0 0 2 Ascending (1) Descending (2) 
2px rgba(0, 0, 0, 0.5);
    }
  `}
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: 48px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgb(30, 28, 28);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in-out;
  z-index: 15;
`;

const TextColorsContainer = styled.div<{ $isDefaultPalette?: boolean }>`
  display: flex;
  ${(props) =>
    !props.$isDefaultPalette &&
    `
    grid-template-columns: repeat(auto-fill,minmax(20px,1fr));
    grid-gap: 5px;`};
  width: 100%;
`;

const FontCustomOption = styled.img`
  max-width: 100%;
  height: 24px;
  object-fit: contain;
`;

const FontCustomSingleValue = styled.img`
  max-width: 100%;
  height: 24px;
  object-fit: contain;
`;

const FontCustomSingleValueContainer = styled.div`
  display: flex;
  place-content: center;
  width: 100%;
  height: 100%;
`;

const FontOption = (props: JSX.IntrinsicAttributes & OptionProps<any, boolean, GroupBase<any>>) => {
  return (
    <components.Option {...props}>
      {<FontCustomOption src={props.data.imageUrl} alt={props.data.name} />}
    </components.Option>
  );
};

const FontSingleValue = (
  props: JSX.IntrinsicAttributes & SingleValueProps<any, boolean, GroupBase<any>>
) => {
  return (
    <components.SingleValue {...props}>
      <FontCustomSingleValueContainer>
        {<FontCustomSingleValue src={props.data.imageUrl} alt={props.data.name} />}
      </FontCustomSingleValueContainer>
    </components.SingleValue>
  );
};
const BackWaistItemText: FC<{
  placeholder?: string;
  waistbandTextColor?: string;
  item: EditTextItem;
  handleItemPropChange: PropChangeHandler;
  fonts?: FontFamily[];
  hideRemoveButton?: boolean;
  TextWaistbandController?: (isAdd: boolean) => void;
}> = ({
  item,
  handleItemPropChange,
  hideRemoveButton,
  placeholder,
  waistbandTextColor,
  TextWaistbandController,
}) => {
  const { removeItem, fonts, getPrintingMethodsRestrictions } = useZakeke();

  // console.log(fonts, "fonts");

  const constraints = item.constraints;
  const canEdit = constraints?.canEdit ?? true;
  const hasCurvedText = item.isTextOnPath;
  const isUpperCase = constraints?.toUppercase ?? false;

  // Used for performance cache
  const [fillColor, setFillColor] = useState(item.fillColor);

  const weightData =
    typeof item.fontWeight === 'number' ? ['normal', 'normal'] : item.fontWeight.split(' ');
  const isBold = weightData.length > 1 ? weightData[1] === 'bold' : weightData[0] === 'bold';
  const isItalic = weightData.length > 1 ? weightData[0] === 'italic' : false;
  const [textAreaLength, setTextAreaLength] = useState(item.text.length);
  const [isColorPickerVisible, setIsColorPickerVisible] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let stringWithZeroWidthSpace = e.target.value.replace(/\u200B/g, '');

    // console.log(stringWithZeroWidthSpace.length);

    setTextAreaLength(e.target.value.length);
    if (handleItemPropChange)
      handleItemPropChange(
        item as TextItem,
        'text',
        isUpperCase ? e.currentTarget.value.toUpperCase() : e.currentTarget.value
      );
  };

  const restrictions = getPrintingMethodsRestrictions?.();
  const allowedColors: string[] =
    restrictions?.textColors?.map((c) => c.colorCode.toLowerCase()) ?? defaultColorsPalette;
  const defaultColor =
    restrictions?.textColors?.find((c) => c.isDefault)?.colorCode.toLowerCase() ||
    defaultColorsPalette[1];

  // Initialize fillColor with the default color if item.fillColor is not set
  useState(() => {
    if (!item.fillColor && defaultColor) {
      setFillColor(defaultColor);
      handleItemPropChange(item, 'font-color', defaultColor);
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleFillColorChange = useCallback(
    debounce((color: string) => {
      const lowerCaseColor = color.toLowerCase();
      if (allowedColors.includes(lowerCaseColor)) {
        handleItemPropChange(item, 'font-color', lowerCaseColor);
        setFillColor(lowerCaseColor);
      } else {
        console.warn(`Color ${color} is not allowed by the current printing method.`);
      }
    }, 500),
    [item, handleItemPropChange, allowedColors]
  );
  useEffect(() => {
    if (P.equals(placeholder!, PLACEHOLDERS.BackAreaKeyword) && waistbandTextColor) {
      // Remove "Sparkle" (case-insensitive), then trim
      const cleanedColorName = waistbandTextColor.replace(/sparkle/i, '').trim();

      const matchedColorEntry = Object.entries(colorNameMap).find(
        ([, name]) => name.toLowerCase() === cleanedColorName.toLowerCase()
      );

      if (matchedColorEntry) {
        const matchedColor = matchedColorEntry[0];

        if (matchedColor.toLowerCase() !== fillColor.toLowerCase()) {
          handleFillColorChange(matchedColor);
        }
      }
    }
  }, [waistbandTextColor, fillColor, placeholder, handleFillColorChange]);

  let currentFont = fonts?.find((x) => x.name === item.fontFamily);

  const [fontLoading, setFontLoading] = useState(false);

  const setItemTextDebounced = (value: string) => {
    handleItemPropChange?.(item, 'text', isUpperCase ? value.toUpperCase() : value);
    debounce(() => {
      const initialText = value;

      const text = value;

      if (text !== initialText) {
        handleItemPropChange?.(item, 'text', isUpperCase ? text.toUpperCase() : text);
      }
    }, 500)();
  };
  // console.log(constraints)
  const handleFontChange = (font: string) => {
    handleItemPropChange(item, 'font-family', font);
    currentFont = fonts?.find((x) => x.name === font);
    setItemTextDebounced(item.text);
  };

  if (item)
    return (
      <div>
        <ItemTextContainer>
          <FormControl
            label={item.name || 'TEXT: '}
            detail={item.text.length > 0 ? item.text : 'Enter your text'}
          >
            <TextArea
              value={isUpperCase ? item.text.toUpperCase() : item.text}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault(); // stop newline
                }
              }}
              maxLength={!item.constraints ? null : item.constraints.maxNrChars || null}
              disabled={!canEdit}
            />
          </FormControl>

          {/* <TextToolsContainer> */}
          <div className="item_text">
            {(!constraints || constraints.canChangeFontFamily) && (
              <FormControl label={'FONT: '} detail={`${item.fontFamily}`}>
                <AdvancedSelect
                  components={{
                    Option: FontOption,
                    SingleValue: FontSingleValue,
                  }}
                  styles={{
                    container: (base) =>
                      ({
                        ...base,
                        width: 220,
                        borderRadius: '5px',
                        border: '1px solid black',
                      }) as CSSObjectWithLabel,
                  }}
                  isSearchable={false}
                  options={fonts}
                  isDisabled={fontLoading}
                  menuPosition="fixed"
                  value={[fonts!.find((x) => x.name === item.fontFamily)]}
                  onChange={(font: any) => {
                    item.fontFamily = font.name;
                    setFontLoading(true);
                    handleFontChange(font.name);
                    setTimeout(() => {
                      setFontLoading(false);
                    }, 2000);
                  }}
                />
              </FormControl>
            )}
            <div
              className="backwaistband-css"
              onClick={() => {
                removeItem(item.guid);
                TextWaistbandController?.(true);
              }}
            >
              <svg
                width="22"
                height="30"
                viewBox="0 0 17 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.54594 2.4196L9.63125 1.24826C10.1094 1.11115 10.6104 1.4027 10.7495 1.89909L11.0656 3.02666L5.24779 4.69474L4.93173 3.56717C4.7926 3.07079 5.06775 2.5567 5.54594 2.4196Z"
                  fill="white"
                  stroke="#434342"
                />
                <path
                  d="M14.4476 2.06228L1.86626 5.6696C1.38784 5.80677 1.11285 6.32056 1.25205 6.81717L1.85927 8.98351C1.99847 9.48012 2.49915 9.77151 2.97757 9.63434L15.5589 6.02702C16.0373 5.88985 16.3123 5.37606 16.1731 4.87945L15.5659 2.71311C15.4267 2.2165 14.926 1.92511 14.4476 2.06228Z"
                  fill="white"
                  stroke="#434342"
                />
                <path
                  d="M2.875 11.3594H16.4546V22.9275C16.4546 23.4431 16.0362 23.8616 15.5205 23.8616H3.80908C3.29344 23.8616 2.875 23.4431 2.875 22.9275V11.3594Z"
                  fill="white"
                  stroke="#434342"
                />
                <path d="M6.27344 13.7734V21.4438" stroke="#434342" />
                <path d="M9.67969 13.7734V21.4438" stroke="#434342" />
                <path d="M13.1406 13.7734V21.4438" stroke="#434342" />
              </svg>
            </div>
          </div>
        </ItemTextContainer>
        {!P.equals(placeholder!, PLACEHOLDERS.BackAreaKeyword) &&
          !P.equals(placeholder!, PLACEHOLDERS.FrontAreaKeyword) && (
            <div>
              <FormControl
                label={'TEXT COLOR:'}
                detail={colorNameMap[fillColor.toLowerCase()] || fillColor.toUpperCase()}
              >
                <ColorsContainer>
                  <ColorSelector>
                    {allowedColors.map((c: string) => (
                      <SinglePaletteItem
                        key={c}
                        color={c}
                        selected={c.toLowerCase() === fillColor.toLowerCase()}
                        onClick={() => handleFillColorChange(c)}
                      >
                        <Tooltip className="tooltip">
                          {colorNameMap[c.toLowerCase()] || c.toUpperCase()}
                        </Tooltip>
                      </SinglePaletteItem>
                    ))}
                  </ColorSelector>
                </ColorsContainer>
              </FormControl>
            </div>
          )}
      </div>
    );
  else return null;
};

export default BackWaistItemText;
